<script setup lang="ts">
import { useClipboard } from '@vueuse/core'
import { useClientOs } from '~/composables/use-os'
import type { ClientOs } from '~/composables/use-os'

// Version-independent download shortlinks. The server resolves each to the newest matching
// aiscan-clients release asset, so nothing here pins a release tag.
const DMG_URL = '/api/aiscan/download/desktop-dmg'
const WINDOWS_URL = '/api/aiscan/download/desktop-windows'
const FIREFOX_URL = '/api/aiscan/download/firefox'
// TODO(SK-612): set to the unlisted Chrome Web Store listing URL once published. That's the only
// one-click self-install for unmanaged Chrome - off-store .crx installs are blocked. Until then
// the button stays disabled.
const CHROME_WEBSTORE_URL = ''

const detectedOs = useClientOs()
// Follow the detected OS, but let the user override via the switcher (mobile / unknown falls back
// to macOS, the pilot's most common desktop).
const selectedOs = ref<ClientOs>('mac')
watch(detectedOs, (os) => os !== 'unknown' && (selectedOs.value = os), { immediate: true })

const osTabs: { id: ClientOs; label: string; icon: string }[] = [
  { id: 'mac', label: 'macOS', icon: 'brands:apple' },
  { id: 'windows', label: 'Windows', icon: 'brands:windows' },
  { id: 'linux', label: 'Linux', icon: 'brands:linux' },
]

// Self-detecting terminal install (macOS backup + Linux): the shortlink picks the right build from
// uname, so it never encodes an arch or version. First run signs in, then does an initial sync.
const origin = ref('')
onMounted(() => (origin.value = window.location.origin))
const cliCommand = computed(
  () =>
    `curl -fsSL "${origin.value}/api/aiscan/download/desktop-cli?os=$(uname -s)&arch=$(uname -m)"` +
    ' | tar -xz -C /tmp && mkdir -p ~/.local/bin && mv /tmp/aiscan ~/.local/bin/' +
    ' && ~/.local/bin/aiscan login && ~/.local/bin/aiscan sync',
)

const { copy, copied } = useClipboard({ legacy: true, copiedDuring: 1500 })
</script>

<template>
  <div class="flex flex-col gap-6">
    <p class="text-sm text-gray-600 dark:text-gray-400">
      AI usage lives in two separate places that no single tool can reach, so there are two tools. The
      <strong>desktop app</strong> reads the AI tools running on your computer (Claude Code, Cursor, Codex), which keep
      their history in local files. The <strong>browser extension</strong> reads the AI you use on websites (ChatGPT,
      Claude.ai, Gemini), which lives in the browser and never touches your disk. Install whichever match how you work —
      or both if you use AI in both places.
    </p>

    <div class="rounded-lg border border-gray-200 p-5 dark:border-gray-700">
      <div class="mb-3 flex items-center gap-2">
        <UIcon name="light:desktop" class="size-5 text-gray-500 dark:text-gray-400" />
        <h3 class="font-semibold text-gray-900 dark:text-gray-100">Desktop app</h3>
      </div>
      <p class="mb-4 text-sm text-gray-600 dark:text-gray-400">
        For AI in your terminal or editor — Claude Code, Cursor, Codex.
      </p>

      <div class="mb-4 flex flex-wrap gap-2">
        <UButton
          v-for="tab in osTabs"
          :key="tab.id"
          :icon="tab.icon"
          :color="selectedOs === tab.id ? 'primary' : 'gray'"
          :variant="selectedOs === tab.id ? 'solid' : 'ghost'"
          size="sm"
          @click="selectedOs = tab.id"
        >
          {{ tab.label }}
        </UButton>
      </div>

      <!-- macOS: the double-clickable menu-bar app is the primary path. -->
      <div v-if="selectedOs === 'mac'" class="flex flex-col gap-4">
        <UButton :to="DMG_URL" target="_blank" external icon="light:arrow-down-to-line" class="w-fit">
          Download for Mac
        </UButton>
        <ol class="flex list-decimal flex-col gap-2 pl-5 text-sm text-gray-700 dark:text-gray-300">
          <li>
            Open the downloaded <strong>Aiscan.dmg</strong> and drag <strong>Aiscan</strong> into
            <strong>Applications</strong>.
          </li>
          <li>
            The first launch is blocked because the pilot build isn't signed by Apple yet. Open
            <strong>System Settings → Privacy &amp; Security</strong>, scroll down, click <strong>Open Anyway</strong>,
            then launch Aiscan again. You only do this once.
          </li>
          <li>
            Click the <strong>aiscan icon in your menu bar</strong> and choose <strong>Log in</strong>. After you
            approve in the browser, it syncs on its own in the background — there's nothing else to run.
          </li>
        </ol>
        <div class="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
          <p class="mb-2 text-sm text-gray-500 dark:text-gray-400">Prefer the terminal? Install the CLI instead:</p>
          <div class="flex items-center gap-2">
            <code class="flex-1 overflow-x-auto whitespace-nowrap font-mono text-sm text-gray-800 dark:text-gray-200">
              {{ cliCommand }}
            </code>
            <UButton
              :icon="copied ? 'light:check' : 'light:copy'"
              variant="ghost"
              size="sm"
              @click="copy(cliCommand)"
            />
          </div>
        </div>
      </div>

      <!-- Windows: no signed tray app yet, so it's the CLI zip. -->
      <div v-else-if="selectedOs === 'windows'" class="flex flex-col gap-4">
        <UButton :to="WINDOWS_URL" target="_blank" external icon="light:arrow-down-to-line" class="w-fit">
          Download for Windows
        </UButton>
        <ol class="flex list-decimal flex-col gap-2 pl-5 text-sm text-gray-700 dark:text-gray-300">
          <li>Unzip the download, then open a terminal (PowerShell) in that folder.</li>
          <li>
            Run <code class="rounded bg-gray-100 px-1 font-mono dark:bg-gray-800">.\aiscan.exe login</code> to sign in,
            then <code class="rounded bg-gray-100 px-1 font-mono dark:bg-gray-800">.\aiscan.exe sync</code> to upload
            your usage.
          </li>
        </ol>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          <code class="rounded bg-gray-100 px-1 font-mono dark:bg-gray-800">sync</code> runs once. To keep it syncing in
          the background automatically, run
          <code class="rounded bg-gray-100 px-1 font-mono dark:bg-gray-800">.\aiscan.exe daemon</code>
          instead.
        </p>
      </div>

      <!-- Linux: terminal install. -->
      <div v-else class="flex flex-col gap-4">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Paste this into a terminal — it installs, signs you in, and syncs:
        </p>
        <div
          class="flex items-center gap-2 rounded-lg border border-gray-300 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800"
        >
          <code class="flex-1 overflow-x-auto whitespace-nowrap font-mono text-sm text-gray-800 dark:text-gray-200">
            {{ cliCommand }}
          </code>
          <UButton :icon="copied ? 'light:check' : 'light:copy'" variant="ghost" size="sm" @click="copy(cliCommand)" />
        </div>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          This runs <code class="rounded bg-gray-100 px-1 font-mono dark:bg-gray-800">login</code> then
          <code class="rounded bg-gray-100 px-1 font-mono dark:bg-gray-800">sync</code> once. To keep it syncing in the
          background automatically, run
          <code class="rounded bg-gray-100 px-1 font-mono dark:bg-gray-800">aiscan daemon</code>
          instead.
        </p>
      </div>
    </div>

    <div class="rounded-lg border border-gray-200 p-5 dark:border-gray-700">
      <div class="mb-1 flex items-center gap-2">
        <UIcon name="light:globe" class="size-5 text-gray-500 dark:text-gray-400" />
        <h3 class="font-semibold text-gray-900 dark:text-gray-100">Browser extension</h3>
      </div>
      <p class="mb-3 text-sm text-gray-600 dark:text-gray-400">
        For AI in your browser — ChatGPT, Claude.ai, Gemini. Add it to your browser, then sign in as prompted.
      </p>
      <div class="flex flex-wrap gap-2">
        <UButton :to="FIREFOX_URL" target="_blank" external color="gray" variant="solid" icon="light:globe">
          Add to Firefox
        </UButton>
        <UButton
          :to="CHROME_WEBSTORE_URL || undefined"
          :disabled="!CHROME_WEBSTORE_URL"
          target="_blank"
          external
          color="gray"
          variant="solid"
          icon="light:globe"
        >
          {{ CHROME_WEBSTORE_URL ? 'Add to Chrome' : 'Chrome — coming soon' }}
        </UButton>
      </div>
    </div>
  </div>
</template>
